import type { AnswerRecord, FindingRecord, PhotoRecord } from '@/shared/offline';

/**
 * Waar een foto die nog niet op de server staat op wacht.
 *
 * - `waitingForAnswer`: het antwoord (of de bevinding) moet eerst naar de server;
 * - `withoutAnswer`: de vraag heeft geen antwoord meer - de foto kan nergens heen;
 * - `queued`: aan de beurt zodra er verbinding is;
 * - `sending`: de upload loopt;
 * - `retrying`: het lukte nog niet, er komt vanzelf een nieuwe poging;
 * - `refused`: de server nam hem niet aan, of hij is hier niet meer te lezen.
 */
export type QueuedPhotoStatus =
    | { kind: 'waitingForAnswer' }
    | { kind: 'withoutAnswer' }
    | { kind: 'queued' }
    | { kind: 'sending' }
    | { kind: 'retrying'; error: string; nextAttemptAt: number }
    | { kind: 'refused'; error: string | null };

export interface QueuedPhoto {
    clientUuid: string;
    caption: string;
    fileName: string;
    status: QueuedPhotoStatus;
    /** Nu meteen opnieuw: bij een fout, of bij een foto die zijn backoff afwacht. */
    canRetry: boolean;
    /** Niet meesturen: alleen waar wachten niets meer oplevert. */
    canSkip: boolean;
}

/** Vanaf zoveel mislukte pogingen staat de reden erbij; één keer mislukken is gewoon het netwerk. */
const SHOW_ERROR_AFTER = 2;

/**
 * De foto's van deze opname die nog naar de server moeten, met per foto wat er
 * speelt. In de volgorde waarin ze gemaakt zijn.
 */
export function photoQueue(
    photos: readonly PhotoRecord[],
    answers: readonly AnswerRecord[],
    findings: readonly Pick<FindingRecord, 'findingUuid' | 'confirmed'>[],
): QueuedPhoto[] {
    const answersBySlot = new Map(answers.map((answer) => [answer.slot, answer]));
    const findingsByUuid = new Map(findings.map((finding) => [finding.findingUuid, finding]));

    return photos
        .filter((photo) => photo.deleted !== 1 && photo.state !== 'uploaded' && photo.blob !== null)
        .sort((a, b) => a.createdAt - b.createdAt)
        .map((photo) => {
            const status = statusOf(photo, answersBySlot, findingsByUuid);

            return {
                clientUuid: photo.clientUuid,
                caption: photo.caption ?? '',
                fileName: photo.fileName,
                status,
                canRetry: status.kind === 'retrying' || status.kind === 'refused',
                canSkip:
                    status.kind === 'retrying' ||
                    status.kind === 'refused' ||
                    status.kind === 'withoutAnswer',
            };
        });
}

function statusOf(
    photo: PhotoRecord,
    answersBySlot: ReadonlyMap<string, AnswerRecord>,
    findingsByUuid: ReadonlyMap<string, Pick<FindingRecord, 'findingUuid' | 'confirmed'>>,
): QueuedPhotoStatus {
    if (photo.state === 'failed') {
        return { kind: 'refused', error: photo.lastError };
    }

    if (photo.state === 'uploading') {
        return { kind: 'sending' };
    }

    const owner = photo.findingUuid
        ? findingsByUuid.get(photo.findingUuid)
        : answersBySlot.get(photo.slot);

    if (!owner) {
        return photo.findingUuid ? { kind: 'waitingForAnswer' } : { kind: 'withoutAnswer' };
    }

    if (owner.confirmed !== 1) {
        return { kind: 'waitingForAnswer' };
    }

    if (photo.attempts >= SHOW_ERROR_AFTER && photo.lastError) {
        return { kind: 'retrying', error: photo.lastError, nextAttemptAt: photo.nextAttemptAt };
    }

    return { kind: 'queued' };
}
